import React, { useState, useEffect } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Alert, CircularProgress, Typography, MenuItem, Grid, Chip, Tabs, Tab
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon, SwapHoriz as ShiftIcon } from '@mui/icons-material';
import {
  getBranches, getBranchUsers, createBranchManager, createBranchStaff,
  shiftUserBranch, resetBranchUserPassword
} from '../../services/api';
import { isFullAdmin, canShiftBranches } from '../../utils/auth';

const BranchUsers = () => {
  const [tab, setTab] = useState(0);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [managerDialog, setManagerDialog] = useState(false);
  const [shiftDialog, setShiftDialog] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [managerForm, setManagerForm] = useState({ username: '', full_name: '', email: '', password: '', branch_id: '' });
  const [shiftForm, setShiftForm] = useState({ branch_id: '' });
  const [passwordForm, setPasswordForm] = useState({ password: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [bRes, uRes] = await Promise.all([getBranches(), getBranchUsers()]);
      setBranches(bRes.data || []);
      setUsers(uRes.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateManager = async () => {
    try {
      setError(null);
      if (!managerForm.username || !managerForm.email || !managerForm.password || !managerForm.branch_id) {
        setError('Username, email, password and branch are required');
        return;
      }
      await createBranchManager({
        ...managerForm,
        branch_id: parseInt(managerForm.branch_id, 10)
      });
      setManagerDialog(false);
      setManagerForm({ username: '', full_name: '', email: '', password: '', branch_id: '' });
      setSuccess('Branch manager created successfully');
      loadData();
    } catch (err) {
      setError(err.message || err.error || 'Failed to create branch manager');
    }
  };

  const handleShift = async () => {
    try {
      await shiftUserBranch(selectedUser.id, shiftForm);
      setShiftDialog(false);
      setSuccess('User shifted to new branch');
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleResetPassword = async () => {
    try {
      await resetBranchUserPassword(selectedUser.id, passwordForm);
      setPasswordDialog(false);
      setPasswordForm({ password: '' });
      setSuccess('Password updated');
    } catch (err) {
      setError(err.message);
    }
  };

  const branchName = (id) => branches.find((b) => b.id === id)?.name || '-';
  const filteredUsers = tab === 0
    ? users.filter((u) => u.role === 'branch_manager')
    : users.filter((u) => u.role !== 'admin' && u.role !== 'branch_manager');

  if (!canShiftBranches()) {
    return <Alert severity="error">Only Super Admin, Admin or Super Accountant can manage branch users.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={600}>👤 Branch User Management</Typography>
          <Typography color="text.secondary">Create branch managers and shift users between branches</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<RefreshIcon />} onClick={loadData}>Refresh</Button>
          {tab === 0 && isFullAdmin() && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setManagerDialog(true)}>
              Create Branch Manager
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Branch Managers" />
        <Tab label="All Branch Users" />
      </Tabs>

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Branch</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((u) => (
                <TableRow key={u.id}>
                  <TableCell><strong>{u.username}</strong></TableCell>
                  <TableCell>{u.full_name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell><Chip label={u.role} size="small" /></TableCell>
                  <TableCell>{branchName(u.branch_id)}</TableCell>
                  <TableCell>
                    <Chip label={u.is_active !== false ? 'Active' : 'Inactive'} size="small" color={u.is_active !== false ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell>
                    <Button size="small" startIcon={<ShiftIcon />} onClick={() => { setSelectedUser(u); setShiftForm({ branch_id: u.branch_id || '' }); setShiftDialog(true); }}>
                      Shift Branch
                    </Button>
                    <Button size="small" onClick={() => { setSelectedUser(u); setPasswordDialog(true); }}>Reset Password</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={managerDialog} onClose={() => setManagerDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Branch Manager</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}><TextField fullWidth label="Username" value={managerForm.username} onChange={(e) => setManagerForm({ ...managerForm, username: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Full Name" value={managerForm.full_name} onChange={(e) => setManagerForm({ ...managerForm, full_name: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Email" type="email" value={managerForm.email} onChange={(e) => setManagerForm({ ...managerForm, email: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Password" type="password" value={managerForm.password} onChange={(e) => setManagerForm({ ...managerForm, password: e.target.value })} /></Grid>
            <Grid item xs={12}>
              <TextField select fullWidth label="Branch" value={managerForm.branch_id} onChange={(e) => setManagerForm({ ...managerForm, branch_id: e.target.value })}>
                {branches.map((b) => <MenuItem key={b.id} value={b.id}>{b.name} ({b.branch_code})</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManagerDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateManager}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={shiftDialog} onClose={() => setShiftDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Shift User to Branch</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Move <strong>{selectedUser?.full_name || selectedUser?.username}</strong> to another branch</Typography>
          <TextField select fullWidth label="New Branch" value={shiftForm.branch_id} onChange={(e) => setShiftForm({ branch_id: e.target.value })}>
            {branches.map((b) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShiftDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleShift}>Shift</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={passwordDialog} onClose={() => setPasswordDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="New Password" type="password" value={passwordForm.password} onChange={(e) => setPasswordForm({ password: e.target.value })} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleResetPassword}>Update</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BranchUsers;
